
import { Loader2 } from "lucide-react";
import { TenderCard } from "./TenderCard";
import { TenderMatch } from "@/pages/TenderAgent";
import { useTranslation } from "react-i18next";
import { safeParseDate } from "@/utils/dateValidation";

interface TenderListProps {
  tenders: TenderMatch[] | undefined;
  isLoading: boolean;
  onTenderClick: (tender: TenderMatch) => void;
  onSaveTender?: (tender: TenderMatch) => Promise<void>;
}

export const TenderList = ({ 
  tenders, 
  isLoading, 
  onTenderClick,
  onSaveTender
}: TenderListProps) => {
  const { t } = useTranslation("tenderAgent");
  
  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!tenders?.length) {
    return (
      <div className="text-center py-8 text-gray-600">
        {t("tenderList.noTenders")}
      </div>
    );
  }

  // Filter out tenders with passed deadlines
  const currentDate = new Date();
  console.log("Current date:", currentDate);
  console.log("Total tenders before filtering:", tenders.length);

  const validTenders = tenders.filter(tender => {
    // Validate and log the tender ID fields to help with debugging
    if (!tender.id && !tender.publication_number) {
      console.warn("Tender missing ID fields:", tender);
    }
    
    // Get main deadline from DT field
    const deadlineStr = tender.DT?.[0];
    
    if (!deadlineStr) {
      return true; // Keep tenders without deadlines
    }

    try {
      // Try to safely parse the date instead of directly creating a Date object
      const deadlineDate = safeParseDate(deadlineStr);
      
      // If parsing failed or the date is in the future, keep the tender
      return !deadlineDate || deadlineDate > currentDate;
    } catch (error) {
      console.error(`Error parsing deadline for tender ${tender.id || tender.publication_number}:`, error);
      return true; // Keep tenders with unparseable deadlines
    }
  });

  // Sort tenders by deadline, closest first
  const sortedTenders = validTenders.sort((a, b) => {
    const aDeadline = a.DT?.[0];
    const bDeadline = b.DT?.[0];
    
    // If no deadline, put at the end
    if (!aDeadline) return 1;
    if (!bDeadline) return -1;
    
    // Try to safely parse the dates
    const aDate = safeParseDate(aDeadline);
    const bDate = safeParseDate(bDeadline);
    
    // Handle cases where dates couldn't be parsed
    if (!aDate) return 1;
    if (!bDate) return -1;
    
    // Sort by date ascending (closest deadline first)
    return aDate.getTime() - bDate.getTime();
  });

  console.log("Filtered tenders:", sortedTenders.length, "out of", tenders.length);

  return (
    <div className="grid gap-4">
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-md">
        <p className="text-sm text-blue-700">
          {t("tenderList.showing", { shown: sortedTenders.length, total: tenders.length })}
        </p>
      </div>
      
      {sortedTenders.length > 100 && (
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-md mb-4">
          <p className="text-sm text-yellow-700">
            {t("tenderList.largeNumberWarning")}
          </p>
        </div>
      )}
      
      {sortedTenders.map((tender) => (
        <TenderCard
          key={tender.id || tender.publication_number || Math.random().toString()}
          tender={tender}
          onClick={() => onTenderClick(tender)}
          onSaveTender={onSaveTender ? () => onSaveTender(tender) : undefined}
          viewMode="agent"
        />
      ))}
    </div>
  );
};
